<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '../stores/app';
import { personalInfo } from '../data/portfolio';

const appStore = useAppStore();
const mounted = ref(false);
const embers = ref<Array<{ id: number; left: number; delay: number; duration: number; x: number }>>([]);

const displayName = appStore.isZh ? personalInfo.name : personalInfo.nameEn;
const displayTitle = appStore.isZh ? personalInfo.title : personalInfo.titleEn;
const displayTagline = appStore.isZh ? personalInfo.tagline : personalInfo.taglineEn;
const heroAvatar = personalInfo.heroImage;

onMounted(() => {
  mounted.value = true;
  for (let i = 0; i < 24; i++) {
    embers.value.push({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 4 + Math.random() * 5,
      x: (Math.random() - 0.5) * 160
    });
  }
});

onUnmounted(() => {
  embers.value = [];
});

function scrollToAbout() {
  appStore.scrollToSection('about');
}
</script>

<template>
  <section id="home" class="nz-hero">
    <!-- 背景层：纯 CSS，无外部图片依赖 -->
    <div class="hero-bg"></div>
    <div class="hero-grid"></div>
    <div class="hero-flame-glow"></div>
    <div class="hero-overlay"></div>

    <!-- 火星粒子 -->
    <div class="ember-container">
      <div
        v-for="ember in embers"
        :key="'e-' + ember.id"
        class="ember"
        :style="{
          left: ember.left + '%',
          animationDelay: ember.delay + 's',
          animationDuration: ember.duration + 's',
          '--ember-x': ember.x + 'px'
        }"
      ></div>
    </div>

    <!-- 主体：左右分栏 -->
    <div class="hero-inner" :class="{ 'is-mounted': mounted }">
      <!-- 左栏：文案 -->
      <div class="hero-copy">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          <span class="badge-text">{{ appStore.isZh ? '魔童降世 · 代码由我主宰' : 'Born of chaos, master of code' }}</span>
        </div>

        <h1 class="hero-title">
          <span class="title-char" v-for="(ch, i) in displayName" :key="i" :style="{ animationDelay: 0.3 + i * 0.08 + 's' }">{{ ch }}</span>
        </h1>

        <div class="hero-subtitle">
          <span class="subtitle-line"></span>
          <span class="subtitle-text">{{ displayTitle }}</span>
          <span class="subtitle-line"></span>
        </div>

        <p class="hero-tagline">{{ displayTagline }}</p>

        <div class="hero-actions">
          <button class="nezha-btn nezha-btn-primary" @click="scrollToAbout">
            <i class="fas fa-fire"></i>
            {{ appStore.isZh ? '进入我的世界' : 'Enter My World' }}
          </button>
          <a
            href="https://github.com/ctkqiang"
            target="_blank"
            rel="noopener noreferrer"
            class="nezha-btn nezha-btn-outline"
          >
            <i class="fab fa-github"></i>
            GitHub
          </a>
        </div>

        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-value">50+</span>
            <span class="stat-label">{{ appStore.isZh ? '开源项目' : 'Projects' }}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-value">12+</span>
            <span class="stat-label">{{ appStore.isZh ? '年研发经验' : 'Years' }}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-value">2+2</span>
            <span class="stat-label">CVE / CNVD</span>
          </div>
        </div>
      </div>

      <!-- 右栏：乾坤圈 + 头像 -->
      <div class="hero-visual">
        <div class="cosmic-ring ring-1"></div>
        <div class="cosmic-ring ring-2"></div>
        <div class="ring-serration s1"></div>
        <div class="ring-serration s2"></div>
        <div class="ring-serration s3"></div>
        <div class="ring-serration s4"></div>

        <div class="hero-avatar-wrap">
          <div class="hero-avatar-ring"></div>
          <div class="hero-avatar">
            <img :src="heroAvatar" alt="NeZha Avatar" class="avatar-img" />
          </div>
        </div>

        <div class="visual-chip chip-top">
          <i class="fas fa-shield-alt"></i>
          <span>Security</span>
        </div>
        <div class="visual-chip chip-bottom">
          <i class="fas fa-bolt"></i>
          <span>High-Perf</span>
        </div>
      </div>
    </div>

    <!-- 滚动提示 -->
    <div class="scroll-hint" @click="scrollToAbout">
      <span class="hint-text">{{ appStore.isZh ? '向下滚动' : 'Scroll Down' }}</span>
      <div class="hint-arrow">
        <i class="fas fa-chevron-down"></i>
      </div>
    </div>
  </section>
</template>

<style scoped>
@import '../styles/components/hero.css';
</style>
